import { LitElement, css, html } from 'lit';
import { property, customElement, state } from 'lit/decorators.js';
import { resolveRouterPath } from '../router';
import { authService } from '../services/auth-service';
import { supabase } from '../services/supabase';

import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';

@customElement('app-nav')
export class AppNav extends LitElement {
  @property() activeNav = 'home';

  @state()
  private isLoggedIn = false;

  static styles = css`
    :host {
      display: block;
      direction: rtl;
    }

    header {
      padding: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      background: var(--background);
      border-bottom: 1px solid var(--border);
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      height: 60px;
      z-index: 100; /* Above sidebar (z-index: 10) */
    }

    .nav-container {
      display: flex;
      gap: 32px; /* Standard spacing scale (8 increments) */
      align-items: center;
      justify-content: space-between; /* Home on right, profile on left (RTL) */
      width: 100%;
      padding: 0 6px; /* Center icons within 48px sidebar width */
    }

    .nav-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      cursor: pointer;
      transition: all 0.3s ease;
      text-decoration: none;
      color: var(--muted-foreground);
    }

    .nav-item:first-child {
      margin-right: 0; /* Home icon - will be centered in 48px from right edge */
    }

    .nav-item sl-icon-button {
      font-size: 1.5rem;
      color: var(--muted-foreground);
    }

    .nav-item sl-icon-button::part(base) {
      color: var(--muted-foreground);
    }

    .nav-item.active sl-icon-button::part(base) {
      color: var(--foreground);
    }

    .nav-label {
      display: none;
    }

    /* Mobile: Keep navigation at bottom */
    @media (max-width: 768px) {
      header {
        top: auto;
        bottom: 0;
        border-top: 1px solid var(--border);
        border-bottom: none;
        z-index: 1000;
      }

      .nav-container {
        gap: 12px; /* Smaller gap on mobile but still consistent */
        justify-content: space-around; /* Even distribution on mobile */
        padding: 0 6px; /* Match desktop padding */
      }
    }
  `;

  async connectedCallback() {
    super.connectedCallback();

    // Check initial auth state
    await this.checkAuthState();

    // Listen for auth state changes
    supabase.auth.onAuthStateChange((event, session) => {
      console.log('🔐 Nav: Auth state changed', { event, hasSession: !!session });
      this.isLoggedIn = !!session;
      this.requestUpdate();
    });
  }

  private async checkAuthState() {
    const { data: { session } } = await supabase.auth.getSession();
    this.isLoggedIn = !!session;
    console.log('👤 Nav: Initial auth check', { isLoggedIn: this.isLoggedIn });
  }

  handleNavClick(navItem: string, e: Event) {
    e.preventDefault();
    this.activeNav = navItem;
    if (navItem === 'brainstorm') {
      window.location.href = resolveRouterPath('brainstorm');
    } else if (navItem === 'calendar') {
      window.location.href = resolveRouterPath('calendar');
    } else if (navItem === 'home') {
      window.location.href = resolveRouterPath('');
    } else if (navItem === 'profile') {
      window.location.href = resolveRouterPath('login');
    }
  }

  async handleLogout(e: Event) {
    e.preventDefault();
    console.log('🚪 Logging out...');
    await authService.signOut();
    this.isLoggedIn = false;
    window.location.href = resolveRouterPath('login');
  }

  render() {
    return html`
      <header>
        <nav class="nav-container">
          <a class="nav-item ${this.activeNav === 'home' ? 'active' : ''}" href="${resolveRouterPath('')}" @click="${(e: Event) => this.handleNavClick('home', e)}">
            <sl-icon-button name="${this.activeNav === 'home' ? 'house-fill' : 'house'}" label="الرئيسية"></sl-icon-button>
          </a>

          ${this.isLoggedIn
            ? html`
                <a class="nav-item profile logged-in active" href="${resolveRouterPath('login')}" @click="${(e: Event) => this.handleNavClick('profile', e)}">
                  <sl-icon-button name="person-fill-check" label="الملف الشخصي"></sl-icon-button>
                </a>
              `
            : html`
                <a class="nav-item profile ${this.activeNav === 'profile' ? 'active' : ''}" href="${resolveRouterPath('login')}" @click="${(e: Event) => this.handleNavClick('profile', e)}">
                  <sl-icon-button name="${this.activeNav === 'profile' ? 'person-fill' : 'person'}" label="تسجيل الدخول"></sl-icon-button>
                </a>
              `
          }
        </nav>
      </header>
    `;
  }
}
